// v3
// DeltaBadge.js
// Drop this anywhere you want to show a +/- change indicator.
// Usage: <DeltaBadge delta={-3} /> or <DeltaBadge delta={5} suffix="%" />
// Use invertColors for metrics where higher is worse (e.g. Entity Gap)
// invertColors flips the colour only — arrow and number are always truthful

import React from 'react';

export function DeltaBadge({ delta, suffix = '', size = 'sm', invertColors = false }) {
  const sizes = {
    sm: { fontSize: '11px', padding: '2px 6px', gap: '2px', height: '20px' },
    md: { fontSize: '13px', padding: '3px 8px', gap: '3px', height: '24px' },
  };

  // Always reserve the height so the row stays aligned
  if (delta === null || delta === undefined) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: sizes[size].height,
        fontSize: sizes[size].fontSize,
        fontWeight: 600,
        padding: sizes[size].padding,
        borderRadius: '4px',
        letterSpacing: '0.06em',
        backgroundColor: '#e0f2fe',
        color: '#0284c7',
      }}>
        NEW
      </span>
    );
  }

  const isNeutral = delta === 0;
  const isPositive = delta > 0;
  // isGood drives colour — flipped for invertColors metrics
  const isGood = invertColors ? !isPositive : isPositive;

  const styles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: sizes[size].gap,
    fontSize: sizes[size].fontSize,
    fontWeight: 600,
    padding: sizes[size].padding,
    borderRadius: '4px',
    letterSpacing: '0.02em',
    ...(isNeutral
      ? { backgroundColor: '#fff3e0', color: '#f4a261' }
      : isGood
      ? { backgroundColor: '#dcfce7', color: '#16a34a' }
      : { backgroundColor: '#fee2e2', color: '#dc2626' }),
  };

  // Arrow and sign always reflect the actual direction of the number
  const arrow = isNeutral ? '' : isPositive ? '↑' : '↓';
  const sign = isPositive ? '+' : '';
  const label = isNeutral ? 'NC' : `${arrow} ${sign}${delta}${suffix}`;

  return <span style={styles}>{label}</span>;
}


// ─────────────────────────────────────────────────────────────────────────────
// AuditSummaryPanel
// ─────────────────────────────────────────────────────────────────────────────

const MODULE_LABELS = {
  'llm-audit':          'LLM Visibility',
  'schema-audit':       'Schema',
  'svo-clarity-audit':  'SVO Clarity',
  'nlp-entity-audit':   'NLP Entities',
  'heading-audit':      'Headings',
  'alt-text-audit':     'Alt Text',
  'content-graph':      'Content Graph',
  'freshness-audit':    'Freshness',
};

function ScoreCell({ label, current, delta }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '12px 16px',
      background: '#fff',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      minWidth: '120px',
    }}>
      <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '22px', fontWeight: 700, color: current === null ? '#cbd5e1' : '#0f172a' }}>
          {current !== null ? current : '—'}
        </span>
        {delta !== null && <DeltaBadge delta={delta} />}
      </div>
    </div>
  );
}

export function AuditSummaryPanel({ campaignId, apiBase = '' }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    fetch(`${apiBase}/api/campaigns/${campaignId}/audit-summary`)
      .then(r => r.json())
      .then(json => {
        if (json.success) setData(json);
        else setError(json.error || 'Failed to load');
      })
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
  }, [campaignId, apiBase]);

  if (loading) return (
    <div style={{ padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
      Loading audit summary…
    </div>
  );

  if (error) return (
    <div style={{ padding: '16px', color: '#dc2626', fontSize: '13px' }}>
      {error}
    </div>
  );

  if (!data || !Object.keys(data.modules).length) return (
    <div style={{ padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
      No audit data yet for this campaign.
    </div>
  );

  const lastUpdated = Object.values(data.modules)
    .map(m => m.lastRunAt)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
          Audit Summary
        </span>
        {lastUpdated && (
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
            Last run: {new Date(lastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            {' · '}
            vs {data.daysBack}d ago
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {Object.entries(data.modules).map(([moduleType, scores]) => (
          <ScoreCell
            key={moduleType}
            label={MODULE_LABELS[moduleType] || moduleType}
            current={scores.current}
            delta={scores.delta}
          />
        ))}
      </div>
    </div>
  );
}
